import BaseService from './base.service';

export default class OrgService {
    static ENDPOINT = '/orgs';

    static async getOrgs() {
        const endpoint = `${this.ENDPOINT}`;
        return BaseService.get(endpoint);
    }

    static async getOrgById(orgId) {
        const endpoint = `${this.ENDPOINT}/${orgId}`;
        return BaseService.get(endpoint);
    }

    static async createOrg(orgData) {
        const endpoint = `${this.ENDPOINT}`;
        return BaseService.post(endpoint, orgData);
    }

    static async updateOrg(orgId, updateOrgData) {
        const endpoint = `${this.ENDPOINT}/${orgId}`;
        return BaseService.patch(endpoint, updateOrgData);
    }

    static async updateCa4kConfig(orgId, ca4kConfigData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/ca4k-config`;
        return BaseService.patch(endpoint, ca4kConfigData);
    }

    static async previewCa4kSync(orgId) {
        const endpoint = `/ca4k${this.ENDPOINT}/${orgId}/sync/preview`;
        return BaseService.post(endpoint);
    }

    static async applyCa4kSync(orgId) {
        const endpoint = `/ca4k${this.ENDPOINT}/${orgId}/sync/apply`;
        return BaseService.post(endpoint);
    }

    static async addOrgManager(orgId, managerData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/manager`;
        return BaseService.post(endpoint, managerData);
    }

    static async updatePrimaryManager(orgId, managerId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/update-primary-manager/${managerId}`;
        return BaseService.patch(endpoint);
    }

    static async updateOrgManager(orgId, managerId, managerData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/manager/${managerId}`;
        return BaseService.patch(endpoint, managerData);
    }

    static async removeOrgManager(orgId, userId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/manager/${userId}`;
        return BaseService.delete(endpoint);
    }

    static async updateOrgRolesModules(orgId, orgData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/modules-roles`;
        return BaseService.patch(endpoint, orgData);
    }

    static async setManageAgTs(orgId, enabled) {
        const endpoint = `${this.ENDPOINT}/${orgId}/manage-ag-ts`;
        return BaseService.patch(endpoint, { enabled });
    }

    static async updateLprLicenseConfig(orgId, lprLicenseConfigData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/lpr-license-config`;
        return BaseService.patch(endpoint, lprLicenseConfigData);
    }

    static async setLprDeviceType(orgId, enabled) {
        const endpoint = `${this.ENDPOINT}/${orgId}/lpr-device-type`;
        return BaseService.patch(endpoint, { enabled });
    }

    static async setAssociateAppAccess(orgId, enabled) {
        const endpoint = `${this.ENDPOINT}/${orgId}/associate-app-access`;
        return BaseService.patch(endpoint, { enabled });
    }

    /**
     * Super admin: sets the seat limits of an org (`org.seats`). `seats` holds any of `manager`, `guard`,
     * `virtual_guard` (whole numbers of 1 or more, at least one); the limits that are left out stay as they are.
     * Replaces the older `PATCH /max-managers`.
     */
    static async setSeats(orgId, seats) {
        const endpoint = `${this.ENDPOINT}/${orgId}/seats`;
        return BaseService.patch(endpoint, seats);
    }

    static async setSupervisorApprovalRequired(orgId, enabled) {
        const endpoint = `${this.ENDPOINT}/${orgId}/supervisor-approval`;
        return BaseService.patch(endpoint, { enabled });
    }

    static async setNotificationPreferences(orgId, data) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-preferences`;
        return BaseService.patch(endpoint, data);
    }

    static async getOrgOtcs(orgId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs`;
        return BaseService.get(endpoint);
    }

    static async getOrgOtcById(orgId, codeId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs/${codeId}`;
        return BaseService.get(endpoint);
    }

    static async createOrgOtc(orgId, otcData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs`;
        return BaseService.post(endpoint, otcData);
    }

    static async updateOrgOtc(orgId, codeId, otcData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs/${codeId}`;
        return BaseService.patch(endpoint, otcData);
    }

    static async revokeOrgOtc(orgId, codeId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs/${codeId}/revoke`;
        return BaseService.patch(endpoint, null);
    }

    static async revokeMultipleOrgOtcs(orgId, codeIds) {
        const endpoint = `${this.ENDPOINT}/${orgId}/otcs/revoke-multiple`;
        return BaseService.post(endpoint, { code_ids: codeIds });
    }

    static async checkUserExists(orgId, userData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/check-user`;
        return BaseService.post(endpoint, userData);
    }

    static async updateDeviceFacilityCodes(orgId, deviceFacilityCodes) {
        const endpoint = `${this.ENDPOINT}/${orgId}/device-facility-codes`;
        return BaseService.patch(endpoint, {
            device_facility_codes: deviceFacilityCodes
        });
    }

    static async createDeviceType(orgId, deviceTypeData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/device-types`;
        return BaseService.post(endpoint, deviceTypeData);
    }

    static async updateStations(orgId, stations) {
        const endpoint = `${this.ENDPOINT}/${orgId}/stations`;
        return BaseService.patch(endpoint, { stations });
    }

    static async uploadOrgImage(orgId, formData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/image`;
        return BaseService.post(endpoint, formData);
    }

    static async markAlertSeen(orgId, type) {
        return BaseService.patch(`${this.ENDPOINT}/${orgId}/alerts/seen`, { type });
    }

    // mc_allotment is a plain cap on the org doc now, not a pool to move specific numbers in and
    // out of — both of these just move that one field by `amount`; the backend guards the release
    // call against dropping the allotment below the org's live mc_used.
    static async allocateMobileCredentials(orgId, amount) {
        const endpoint = `${this.ENDPOINT}/${orgId}/mobile-credentials/allocate`;
        return BaseService.post(endpoint, { amount });
    }

    static async releaseMobileCredentials(orgId, amount) {
        const endpoint = `${this.ENDPOINT}/${orgId}/mobile-credentials/release`;
        return BaseService.post(endpoint, { amount });
    }

    // Org broadcast — super admin's free-text alert notification to the org's people.
    static async getBroadcastAudience(orgId, roles) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-broadcasts/audience`;
        return BaseService.get(endpoint, { roles: roles.join(',') });
    }

    static async listBroadcasts(orgId, limit = 5) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-broadcasts`;
        return BaseService.get(endpoint, { limit });
    }

    static async sendBroadcast(orgId, data) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-broadcasts`;
        return BaseService.post(endpoint, data);
    }

    static async resumeBroadcast(orgId, broadcastId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-broadcasts/${broadcastId}/resume`;
        return BaseService.post(endpoint);
    }

    static async retryBroadcast(orgId, broadcastId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/notification-broadcasts/${broadcastId}/retry`;
        return BaseService.post(endpoint);
    }

    static async getUserPhotoId(orgId, userId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/users/${userId}/photo-id`;
        return BaseService.get(endpoint);
    }
}
